import { Dispatch, SetStateAction } from 'react';
import { TableCellProps } from '@mui/material/TableCell';
import { ColumnDefinition } from 'shared/components/table/columnDefinition';
import { TableOverflowMenuItem } from 'shared/components/table/hooks';
import RowItem from 'shared/components/table/types/row-item';
import { NestedKeyof } from 'shared/components/table/utils/nested-keyof-utils';

type TableRow<DataType extends object, Key extends NestedKeyof<DataType>> = {
  toggleRowSelection: (checked: boolean, row: RowItem<DataType>) => void;
  handleRowClick: (row: RowItem<DataType>) => void;
  getCursorStyling: (row: RowItem<DataType>) => CursorStyling;
  currentCellProps: (column: ColumnDefinition<DataType, Key>, row: RowItem<DataType>) => TableCellProps;
  isChecked: boolean;
  rowErrorMessage: string | undefined;
};

export type TableRowProps<DataType extends object, Key extends NestedKeyof<DataType>> = {
  columns: ColumnDefinition<DataType, Key>[];
  overflowMenuOptions?: Array<TableOverflowMenuItem<DataType>>;
  onMoreClick: (event: React.MouseEvent<HTMLElement, MouseEvent>, row: RowItem<DataType>) => void;
  clickedRow: DataType | null;
  rowIndex: number;
  row: RowItem<DataType>;

  currentItem?: DataType;
  onItemClick?: (item: DataType) => void;
  rowsAreSelectable?: boolean;
  onSelectedRowsChanged?: (items: DataType[]) => void;
  selectedRows?: RowItem<DataType>[];
  selectedRowsState: RowItem<DataType>[];
  setSelectedRowsState: Dispatch<SetStateAction<RowItem<DataType>[]>>;
  onRowSelected?: (item: DataType) => void;
  onRowDeselected?: (item: DataType) => void;
  getErrorMessageForRow?: (data: DataType) => string | undefined;
  disabled?: boolean;
};

type CursorStyling = { cursor?: string; opacity?: number } | undefined;

const useTableRow = <DataType extends object, Key extends NestedKeyof<DataType>>({
  onItemClick,
  rowsAreSelectable,
  onSelectedRowsChanged,
  selectedRows: selectedRowsProp,
  selectedRowsState,
  setSelectedRowsState,
  onRowSelected,
  onRowDeselected,
  row,
  getErrorMessageForRow,
  disabled,
}: Readonly<TableRowProps<DataType, Key>>): TableRow<DataType, Key> => {
  const selectedRows = selectedRowsProp ?? selectedRowsState;

  const toggleRowSelection = (checked: boolean, row: RowItem<DataType>): void => {
    let newSelectedRows = [...selectedRows];
    if (checked) {
      newSelectedRows = [...newSelectedRows, row];
      onRowSelected?.(row.data);
    } else {
      newSelectedRows = newSelectedRows.filter((selectedRow) => JSON.stringify(selectedRow) !== JSON.stringify(row));
      onRowDeselected?.(row.data);
    }
    if (!selectedRowsProp) {
      setSelectedRowsState(newSelectedRows);
    }
    onSelectedRowsChanged?.(newSelectedRows.map((row) => row.data));
  };

  const handleRowClick = (row: RowItem<DataType>): void => {
    if (onItemClick) {
      if (row.isClickable || row.isClickable === undefined) {
        onItemClick(row.data);
      }
    } else if (rowsAreSelectable) {
      toggleRowSelection(!selectedRows.some((item) => JSON.stringify(item) === JSON.stringify(row)), row);
    }
  };

  const getCursorStyling = (row: RowItem<DataType>): CursorStyling => {
    if (disabled) {
      return { opacity: 0.6 };
    }
    if (onItemClick && (row.isClickable || row.isClickable === undefined)) {
      return { cursor: 'pointer' };
    }
    return undefined;
  };

  const currentCellProps = (column: ColumnDefinition<DataType, Key>, row: RowItem<DataType>): TableCellProps => {
    let currentCellProps: TableCellProps = {};
    if (typeof column.tableCellProps === 'function') {
      currentCellProps = column.tableCellProps(row.data);
    } else if (column.tableCellProps) {
      currentCellProps = column.tableCellProps;
    }

    return currentCellProps;
  };

  const isChecked = selectedRows.some((selectedRow) => JSON.stringify(selectedRow) === JSON.stringify(row));

  const rowErrorMessage = getErrorMessageForRow?.(row.data);

  return {
    toggleRowSelection,
    handleRowClick,
    getCursorStyling,
    currentCellProps,
    isChecked,
    rowErrorMessage,
  };
};

export default useTableRow;
